import { Component, OnInit, Output, EventEmitter, ViewChild, ViewChildren, QueryList, ElementRef, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from "@angular/forms";
import { CurrencyPipe } from "@angular/common";
//import { ColDef, GridOptions } from '@ag-grid-enterprise/all-modules';
import { ColDef, GridOptions } from "ag-grid-community";

import { DateService } from "../../../../common/services/date.service";
import { FeeAdministrationService } from "../../../services/fee-administration.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { CommonDatePickerComponent } from "../../../../common/components/common-date-picker/common-date-picker.component";

import { FeeConfig, FeeMatrix, FeeType, UpdatedFeeRequest } from "../../../models/fee.model";


@Component({
    selector: 'fee-editor',
    templateUrl: './fee-editor.component.html',
    styleUrls: ['./fee-editor.component.scss'],
    providers: [CurrencyPipe],
    standalone: false
})
export class FeeEditorComponent implements OnInit {

	@ViewChildren(CommonDatePickerComponent) datePickers: QueryList<CommonDatePickerComponent>;
	@ViewChild('dialogHeading') dialogHeading: ElementRef<HTMLElement>;

	current?: FeeConfig | null;

	get disableSave(): boolean {
		return this.form == null || this.form.pristine;
	}

	get excelStyles(): any {
		return this._formatter.excelStyles;
	}

	feeData: FeeConfig[];

	get feeMatrix(): FormArray {
		if (this.form == null) {
			return this.buildMatrix();
		}
		let ctrl = this.form.get('feeMatrix') as FormArray;
		if (ctrl == null) {
			return this.buildMatrix();
		}
		return ctrl as FormArray;
	}

	get feeTypes(): FormArray {
		if (this.form == null) {
			return this.buildMatrix();
		}
		let ctrl = this.form.get('feeTypes') as FormArray;
		if (ctrl == null) {
			return this.buildMatrix();
		}
		return ctrl as FormArray;
	}

	@Output() feesSaved: EventEmitter<UpdatedFeeRequest> = new EventEmitter<UpdatedFeeRequest>();

	form: FormGroup = new FormGroup({});
	gridOptions: GridOptions;

	matrixHelp: string = "Leave blank for fees which require the use of a fee matrix based on urbanization.";
	matrixEntryHelp: string = "Fee matrix entries are code-sensitive.  Codes are defined by GIS responses defined in code and cannot be edited or added.";
	dateHelp: string = "New fees will take effect with the Effective From Date.  Effective To Date is shown only to show when the existing fee would end.  Any existing fee changes that would have started after the updated one will be deleted.";


	showModal: boolean = false;

	statusBar = {
		statusPanels: [
			{
				statusPanel: 'agTotalAndFilteredRowCountComponent',
				align: 'right',
			}
		]
	};

	private static readonly FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

	constructor(private _formatter: GridFormatterService, private _admin: FeeAdministrationService, private currencyPipe: CurrencyPipe, private builder: FormBuilder, private _dateService: DateService, private _elementRef: ElementRef<HTMLElement>) { }

	// Traps Tab/Shift+Tab within the dialog while it's open. ag-grid handles Tab internally while
	// moving between its own cells (and calls preventDefault when it does), so this only needs to
	// act at the true edges of the dialog - where ag-grid gives up without preventing default and
	// the keydown bubbles up here - otherwise focus escapes to the page behind the modal.
	@HostListener('keydown', ['$event'])
	onDialogKeyDown(event: KeyboardEvent): void {
		if (!this.showModal || event.key !== 'Tab' || event.defaultPrevented) return;

		const focusable = Array.from(this._elementRef.nativeElement.querySelectorAll<HTMLElement>(FeeEditorComponent.FOCUSABLE_SELECTOR))
			.filter(el => el.offsetParent !== null);
		if (focusable.length === 0) return;

		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const active = document.activeElement;

		if (event.shiftKey && active === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && active === last) {
			event.preventDefault();
			first.focus();
		}
	}

	ngOnInit() {
		this.gridOptions = this._formatter.BuildDefaultGridOptionsWithColumns(this.defineColumns(), false, true, true, true);
		this.gridOptions.pagination = false;
		this.form = this.buildForm();
	}

	

	addMatrixEntry(): void {
		this.feeMatrix.push(this.buildMatrixEntry("", 0.00));
	}

	addTypeEntry(): void {
		this.feeTypes.push(this.buildTypeEntry(""));
	}

	buildForm(): FormGroup {
		return this.builder.group({
			id: [{ value: null, disabled: true }],
			displayName: [{ value: null, disabled: true }],
			effectiveFrom: this.builder.group({ datePicker: [null, [Validators.required]] }),
			effectiveTo: this.builder.group({ datePicker: [null, []] }),
			feeAmount: [null, [Validators.min(0)]],
			expediteFee: [null, [Validators.min(0)]],
			department: [null, [Validators.required]],
			fund: [null, [Validators.required]],
			ali: [null, [Validators.required]],
			program: [null, [Validators.required]],
			revenueSourceCode: [null, [Validators.required]],
			reporting: [null, []],
			accountDescription: [null, [Validators.required]],
			legalReference: [null, [Validators.required]],
			feeMatrix: this.buildMatrix(),
			feeTypes: this.buildMatrix(),
		});
		
	}

	buildMatrix(): FormArray {
		return this.builder.array([]);
	}

	buildMatrixEntry(initialCode: string, initialAmount: number): FormGroup {
		return this.builder.group({
			matrixCode: [{ value: initialCode, disabled: true }],
			matrixAmount: [{ value: initialAmount, disabled: false }]
		})
	}

	buildTypeEntry(initial: string): FormGroup {
		return this.builder.group({
			typeValue: [{ value: initial, disabled: false }]
		})
	}

	close(): void {
		this.showModal = false;
		this.form = this.buildForm();
		this.form.markAsPristine();
	}

	defineColumns(): ColDef[] {
		let columns: ColDef[] = [];
		columns = [
			{
				headerClass: "wrapped",
				headerName: "Display Name",
				headerTooltip: "Display Name",
				field: "displayName",
				tooltipField: "displayName",		
				minWidth: 200,
				maxWidth: 250,
				filter: false
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Effective From",
				headerTooltip: "Effective From",
				field: "effectiveFromDate",
				valueFormatter: this._formatter.formatDate("effectiveFromDate", "MM/dd/yyyy", "N/A", "+0000"),
				tooltipValueGetter: this._formatter.formatDate("effectiveFromDate", "MM/dd/yyyy", "N/A", "+0000"),
				filter: "agDateColumnFilter",
				filterParams: this._formatter.defaultDateFilterParams,
				minWidth: 150,
				maxWidth: 175
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Effective To",
				headerTooltip: "Effective To",
				field: "effectiveToDate",
				valueFormatter: this._formatter.formatDate("effectiveToDate", "MM/dd/yyyy", "No End Date", "+0000"),
				tooltipValueGetter: this._formatter.formatDate("effectiveToDate", "MM/dd/yyyy", "No End Date", "+0000"),
				filter: "agDateColumnFilter",
				filterParams: this._formatter.defaultDateFilterParams,
				minWidth: 150,
				maxWidth: 175
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Fee Amount",
				headerTooltip: "Fee Amount",
				field: "flatFee",
				valueFormatter: this.formatFlatFee(),
				tooltipValueGetter: this.formatFlatFee(), 
				filter: "agNumberColumnFilter",
				filterParams: this._formatter.defaultNumberFilterParams,
				minWidth: 150,
				maxWidth: 200
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Fee Matrix",
				headerTooltip: "Fee Matrix",
				valueGetter: this.formatFeeMatrix(),
				tooltipValueGetter: this.formatFeeMatrix(),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 150,
				maxWidth: 175
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Fee Types",
				headerTooltip: "Fee Types",
				valueGetter: this.formatFeeTypes(),
				tooltipValueGetter: this.formatFeeTypes(),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 150,
				maxWidth: 175
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Expedite Fee Amount",
				headerTooltip: "Expedite Fee Amount",
				field: "expediteFee",
				valueFormatter: this._formatter.formatCurrency("expediteFee"),
				tooltipValueGetter: this._formatter.formatCurrency("expediteFee"),
				filter: "agNumberColumnFilter",
				filterParams: this._formatter.defaultNumberFilterParams,
				minWidth: 150,
				maxWidth: 205
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Legal Reference",
				headerTooltip: "Legal Reference",
				field: "legalReference",
				tooltipField: "legalReference",
				valueFormatter: this._formatter.formatPotentiallyBlankField("legalReference", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("legalReference", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 200,
				maxWidth: 275
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Department",
				headerTooltip: "Department",
				field: "department",
				tooltipField: "department",
				valueFormatter: this._formatter.formatPotentiallyBlankField("department", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("department", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 135,
				maxWidth: 160
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Fund",
				headerTooltip: "Fund",
				field: "fund",
				tooltipField: "fund",
				valueFormatter: this._formatter.formatPotentiallyBlankField("fund", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("fund", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "ALI",
				headerTooltip: "ALI",
				field: "ali",
				tooltipField: "ali",
				valueFormatter: this._formatter.formatPotentiallyBlankField("ali", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("ali", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Program",
				headerTooltip: "Program",
				field: "program",
				tooltipField: "program",
				valueFormatter: this._formatter.formatPotentiallyBlankField("program", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("program", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Revenue Source Code",
				headerTooltip: "Revenue Source Code",
				field: "revenueSourceCode",
				tooltipField: "revenueSourceCode",
				valueFormatter: this._formatter.formatPotentiallyBlankField("revenueSourceCode", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("revenueSourceCode", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 150,
				maxWidth: 175
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Reporting",
				headerTooltip: "Reporting",
				field: "reporting",
				tooltipField: "reporting",
				valueFormatter: this._formatter.formatPotentiallyBlankField("reporting", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("reporting", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Account Description",
				headerTooltip: "Account Description",
				field: "accountDescription",
				tooltipField: "accountDescription",
				valueFormatter: this._formatter.formatPotentiallyBlankField("accountDescription", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("accountDescription", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 150,
				maxWidth: 250
			} as ColDef

		] as ColDef[];

		return columns;
	}

	

	formatFeeMatrix(): any {
		return (obj: any) => {
			let value: string = "";
			if (obj != null && obj.data != null && obj.data["feeMatrices"] != null) {
				if (obj.data["feeMatrices"].length) {
					for (var m = 0; m < obj.data["feeMatrices"].length; m++) {
						if (m != 0) {
							value = `${value},`
						}
						value = `${value} ${obj.data["feeMatrices"][m].feeCode}:${obj.data["feeMatrices"][m].amount}`;
					}
				}
			}
			return value;
		}
	}

	formatFeeTypes(): any {
		return (obj: any) => {
			let value: string = "";
			if (obj != null && obj.data != null && obj.data["feeTypes"] != null) {
				if (obj.data["feeTypes"].length) {
					for (var t = 0; t < obj.data["feeTypes"].length; t++) {
						if (t != 0) {
							value = `${value},`
						}
						value = `${value} ${obj.data["feeTypes"][t].feeType}`
					}
				}
			}
			return value;
		}
	}

	formatFlatFee(): any {
		return (obj: any) => {
			let value: string | null = "";
			if (obj != null && obj.data != null) {
				if (obj.data['flatFee'] != null)
					value = obj.data['flatFee'];
				if (obj.data['feeMatrices'] != null && obj.data["feeMatrices"].length) {
					value = 'Uses Fee Matrix';
				}
				else {
					value = this.currencyPipe.transform(value, 'USD', 'symbol', '1.2-2');
				}
			} else {
				if (obj.data['feeMatrices'] != null && obj.data["feeMatrices"].length) {
					value = 'Uses Fee Matrix';
				}
				else {
					value = this.currencyPipe.transform(value, 'USD', 'symbol', '1.2-2');
				}
			}
			return value ?? "";
		}
	}

	openForm(data: FeeConfig[]): void {
		this.feeData = data;
		this.setFormDefaults();
		this.showModal = true;
		// Deferred so the DOM has updated (inert removed) before we try to focus into it.
		setTimeout(() => this.dialogHeading?.nativeElement.focus(), 0);
	}

	removeMatrix(index: number): void {
		this.feeMatrix.removeAt(index);
	}

	removeType(index: number): void {
		this.feeTypes.removeAt(index);
	}

	save(): void {
		if (this.form.valid) {
			let config: FeeConfig = {
				effectiveFromDate: new Date(),
				identifier: -1,
				id: <string>this.form.get('id').value,
				displayName: <string>this.form.get('displayName').value,
				effectiveToDate: <Date>this.form.get('effectiveTo').get('datePicker').value,
				flatFee: <number>this.form.get('feeAmount').value,
				expediteFee: <number>this.form.get('expediteFee').value,
				department: <string>this.form.get('department').value,
				fund: <string>this.form.get('fund').value,
				ali: <string>this.form.get('ali').value,
				program: <string>this.form.get('program').value,
				revenueSourceCode: <string>this.form.get('revenueSourceCode').value,
				reporting: <string>this.form.get('reporting').value,
				accountDescription: <string>this.form.get('accountDescription').value,
				legalReference: <string>this.form.get('legalReference').value,
				feeMatrices: [],
				feeTypes: [],
				assessedInRICS: this.current.assessedInRICS,
			} as FeeConfig;
			let effFrom = this.form.get('effectiveFrom').get('datePicker').value;
			if (effFrom != null) {
				if (this.form.get('effectiveFrom').get('datePicker').pristine) {
					config.identifier = this.current.identifier; //reuse the id, though this might cause some issues with the API, its a lot simpler than trying to match an exact Date object down to the minute
				}
				config.effectiveFromDate = effFrom;
			}			
			if (this.feeMatrix != null && this.feeMatrix.length > 0) {
				config.feeMatrices = [];
				for (let m = 0; m < this.feeMatrix.controls.length; m++) {
					let item = this.feeMatrix.controls[m];
					let code = item.get('matrixCode').value;
					let amount = item.get('matrixAmount').value;
					let matrix: FeeMatrix = {
						id: -1,
						configId: config.identifier,
						amount: Number(amount),
						feeCode: code
					};
					if (this.current != null) {
						let existing = this.current.feeMatrices.find(m => m.feeCode != null && m.feeCode == matrix.feeCode);
						if (existing != null && config.identifier == this.current.identifier) {
							matrix.id = existing.id;						
						}
					}
					config.feeMatrices.push(matrix);
				}
			}
			if (this.feeTypes != null && this.feeTypes.length > 0) {
				config.feeTypes = [];
				for (let t = 0; t < this.feeTypes.controls.length; t++) {
					let item = this.feeTypes.controls[t];
					let value = item.get('typeValue').value;
					if (value != null) {
						let type: FeeType = {
							id: -1,
							configId: config.identifier,
							feeType: value
						} as FeeType;
						if (this.current != null && this.current.feeTypes != null && t < this.current.feeTypes.length) {
							let existing = this.current.feeTypes[t]; //this is a simplification, based on the UI not supporting adding or removing types, only editing their values
							if (existing != null && config.identifier == this.current.identifier) {
								type.id = existing.id;
							}
						}
						config.feeTypes.push(type);
					}
				}
			}
			let update: UpdatedFeeRequest = new UpdatedFeeRequest(config);
			update.isDirty = true;
			this.feesSaved.emit(update);
			this.close();
		}
	}

	setFormDefaults(): void {
		this.form.reset();
		this.current = null;
		let now = new Date();
		for (let i = 0; i < this.feeData.length; i++) {
			let entry = this.feeData[i];
			let from = new Date(entry.effectiveFromDate);
			let to = (entry.effectiveToDate == null) ? null : new Date(entry.effectiveToDate);
			let lt = now.getTime() > from.getTime();
			let gt = to == null || now.getTime() < to.getTime();
			if (lt && gt) {
				this.current = entry;
			}
			if (this.current != null) break;
		}
		if (this.current != null) {
			if (this.current.effectiveFromDate != null) {
				let ctrl = this.datePickers.find(r => r.controlName == 'effectiveFrom');
				if (ctrl != null) {
					let value = new Date(this.current.effectiveFromDate);
					ctrl.setCtrl(value);
				}
			}			
			if (this.current.effectiveToDate != null) {
				let ctrl = this.datePickers.find(r => r.controlName == 'effectiveTo');
				if (ctrl != null) {
					let value = new Date(this.current.effectiveToDate);
					ctrl.setCtrl(value);
				}
			}
			this.form.get('displayName').setValue(this.current.displayName);
			this.form.get('id').setValue(this.current.id);
			this.form.get('department').setValue(this.current.department);
			this.form.get('fund').setValue(this.current.fund);
			this.form.get('ali').setValue(this.current.ali);
			this.form.get('program').setValue(this.current.program);
			this.form.get('revenueSourceCode').setValue(this.current.revenueSourceCode);
			this.form.get('reporting').setValue(this.current.reporting);
			this.form.get('accountDescription').setValue(this.current.accountDescription);
			this.form.get('feeAmount').setValue(this.current.flatFee);
			this.form.get('expediteFee').setValue(this.current.expediteFee);
			this.form.get('legalReference').setValue(this.current.legalReference);
			if (this.current.feeMatrices != null && this.current.feeMatrices.length > 0) {
				for (let m = 0; m < this.current.feeMatrices.length; m++) {
					this.feeMatrix.push(this.buildMatrixEntry(this.current.feeMatrices[m].feeCode, this.current.feeMatrices[m].amount));
				}
			}
			if (this.current.feeTypes != null && this.current.feeTypes.length > 0) {
				for (let m = 0; m < this.current.feeTypes.length; m++) {
					this.feeTypes.push(this.buildTypeEntry(`${this.current.feeTypes[m].feeType}`));
				}
			}
		}
	}
}
